import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

// Fallback points at PRODUCTION (relative '/api' breaks in the native app).
const API_URL = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';

const api = axios.create({
  baseURL: `${API_URL}/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('kds_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses — a dead session must land on the login screen with
// a calm explanation, never a silently-frozen kitchen display.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only end the session when the token is genuinely expired — a 401
      // with a valid token is a server quirk, not a reason to kill the shift
      let tokenExpired = true;
      try {
        const t = localStorage.getItem('kds_token');
        if (t) {
          const { exp } = JSON.parse(atob(t.split('.')[1]));
          tokenExpired = !exp || exp * 1000 < Date.now();
        }
      } catch { tokenExpired = true; }
      if (tokenExpired) {
        useAuthStore.getState().logout(true);
      }
    }
    return Promise.reject(error);
  }
);

// Kitchen endpoints
export const kitchenApi = {
  // Get active tickets for a station
  getTickets: (station?: string) =>
    api.get('/kitchen/tickets', { params: { station } }),

  // Start an item (mark as being prepared)
  startItem: (itemId: string) =>
    api.post(`/kitchen/items/${itemId}/start`),

  // Complete an item
  completeItem: (itemId: string) =>
    api.post(`/kitchen/items/${itemId}/complete`),

  // Bump entire ticket (mark as complete)
  bumpTicket: (ticketId: string) =>
    api.post(`/kitchen/tickets/${ticketId}/bump`),

  // Mark menu item as 86'd (unavailable)
  markUnavailable: (menuItemId: string, reason?: string) =>
    api.post('/kitchen/86', { menuItemId, reason }),

  // Mark menu item as available again
  markAvailable: (menuItemId: string, quantity?: number) =>
    api.post('/kitchen/available', { menuItemId, quantity }),

  // Get station statistics
  getStats: (station?: string) =>
    api.get('/kitchen/stats', { params: { station } }),

  // Get completed ticket history
  getHistory: (limit?: number) =>
    api.get('/kitchen/history', { params: { limit } }),

  // Bring a bumped ticket back to the board (mis-tap recovery)
  recallTicket: (ticketId: string) =>
    api.post(`/kitchen/tickets/${ticketId}/recall`),

  // Menu items for the 86 board
  getMenuItems: () =>
    api.get('/menu/items'),
};

// Orders endpoints (for fetching order details)
export const ordersApi = {
  getById: (orderId: string) => api.get(`/orders/${orderId}`),
};

// Pre-shift config endpoints (announcements)
export const preshiftConfigApi = {
  // Get active announcements for kitchen staff
  getAnnouncements: (role?: string) =>
    api.get('/preshift-config/announcements', { params: { role: role || 'cook' } }),
};

// Settings storage (local)
export const settingsStorage = {
  get: (key: string) => {
    const value = localStorage.getItem(`kds_${key}`);
    return value ? JSON.parse(value) : null;
  },
  set: (key: string, value: unknown) => {
    localStorage.setItem(`kds_${key}`, JSON.stringify(value));
  },
  remove: (key: string) => {
    localStorage.removeItem(`kds_${key}`);
  },
};

export default api;
